// The list filters and sorts, as pure functions — TODO T36 (2 Sep 2026).
//
// Every filter T36 adds lives IN THE URL, so a reload keeps it and a link can be sent to a
// colleague (design doc § T36, done-when). That makes the URL a small vocabulary — `?status=`,
// `?stage=`, `?tag=`, `?sort=`, `?filter=` — and this file is where a URL value becomes a
// predicate. Pure so the suite can prove the predicates without a browser: the thing that goes
// wrong with a filter is not the rendering, it is a chip that says "Shortlist" and shows
// everyone.
//
// Two of the lists filter here and the rest filter at the service. The search page's candidate
// list and the searches home are unpaged — one mandate's pipeline, one agency's mandates — so
// filtering the rows already on screen is exact. The candidates list and the conversations list
// are CAPPED, so their filters go to the service in the query string (api/candidates.ts,
// api/conversations.ts): a chip that could only see the fifty newest rows would hide exactly the
// old one that needed a human.
import type {
  CandidateSort,
  ConnectionSort,
  GateState,
  PartyType,
  PipelineStage,
  SearchCandidate,
  SearchStatus,
  SearchSummary,
} from '@vera/bff';
import { PIPELINE_STAGES } from './pipeline.ts';

/** The candidates list's sorts, in the order the control offers them (the service's vocabulary). */
export const CANDIDATE_SORTS: readonly CandidateSort[] = ['name', 'entered', 'activity'];

/**
 * The Connections list's sorts — T111. The service's `CONNECTION_SORTS`, restated because the
 * brain imports only types from it; lists.test.ts holds the two equal.
 */
export const CONNECTION_SORTS: readonly ConnectionSort[] = ['name', 'connected', 'added'];

/**
 * A query object with its `undefined` values left out — what a URL with three of six filters
 * set turns into. The domain modules' query types make every filter optional, and under
 * `exactOptionalPropertyTypes` an explicit `undefined` is not "absent".
 */
export function defined<T extends object>(values: { [K in keyof T]?: T[K] | undefined }): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) if (value !== undefined) out[key] = value;
  return out as T;
}

/** A query-string value from a closed vocabulary, or nothing. Unknown values read as "all". */
export function readOneOf<T extends string>(
  raw: string | null | undefined,
  allowed: readonly T[],
): T | undefined {
  return raw !== null && raw !== undefined && (allowed as readonly string[]).includes(raw)
    ? (raw as T)
    : undefined;
}

/** A free-text query-string value, trimmed, or nothing. */
export function readText(raw: string | null | undefined): string | undefined {
  const value = raw?.trim();
  return value ? value : undefined;
}

/**
 * The next URL after a control changes: set the keys given, drop the ones set to `undefined`,
 * keep everything else — so the search page's stage tile does not forget the tag filter.
 */
export function withParams(
  current: URLSearchParams,
  changes: Record<string, string | undefined>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
  }
  return next;
}

// --- the searches home: status chips ------------------------------------

export const SEARCH_STATUSES: readonly SearchStatus[] = [
  'sourcing',
  'active',
  'shortlist',
  'client_review',
  'closed',
];

/**
 * The status chips and, since T273 (5 Oct 2026), a company: the two stack ("Wix" and "Active" is
 * Wix's active Searches). Each narrows alone and leaves the list's order as it was.
 */
export function filterSearches(
  rows: readonly SearchSummary[],
  status: SearchStatus | undefined,
  clientId?: string | undefined,
): SearchSummary[] {
  return rows.filter(
    (row) => (!status || row.status === status) && (!clientId || row.clientId === clientId),
  );
}

/**
 * The Company dropdown's options on the Searches sub-tab (T273): only the companies with people in
 * a Search's pipeline, by name — Natanel: "it should only offer companies that are in that tab".
 */
export function searchCompanies(rows: readonly SearchSummary[]): { id: string; name: string }[] {
  const companies = new Map<string, string>();
  for (const row of rows) {
    if (row.clientId !== null && row.clientName !== null && row.candidateCount > 0) {
      companies.set(row.clientId, row.clientName);
    }
  }
  return [...companies]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// --- the search page: stage tiles, a tag, a sort -------------------------

export type SearchCandidateSort = 'name' | 'stage' | 'activity';
export const SEARCH_CANDIDATE_SORTS: readonly SearchCandidateSort[] = ['name', 'stage', 'activity'];

export function filterSearchCandidates(
  rows: readonly SearchCandidate[],
  filter: {
    stage?: PipelineStage | undefined;
    /** T365b: with a stage, the role's own stage (an id) or none of them (null); unset = either. */
    stepId?: string | null | undefined;
    tagId?: string | undefined;
  },
): SearchCandidate[] {
  return rows.filter(
    (row) =>
      (!filter.stage || row.stage === filter.stage) &&
      (filter.stepId === undefined || (row.step?.id ?? null) === filter.stepId) &&
      (!filter.tagId || row.tags.some((tag) => tag.id === filter.tagId)),
  );
}

/**
 * `stage` is pipeline order (Sourced first), not alphabetical — the enum's order is the funnel.
 * `activity` is newest first with the never-texted last, the same rule the service applies to
 * the candidates list. Stable: a tie keeps the service's order, which is by name.
 */
export function sortSearchCandidates(
  rows: readonly SearchCandidate[],
  sort: SearchCandidateSort,
): SearchCandidate[] {
  const byName = (a: SearchCandidate, b: SearchCandidate) =>
    (a.fullName ?? '').localeCompare(b.fullName ?? '');
  const sorted = [...rows];
  switch (sort) {
    case 'name':
      return sorted.sort(byName);
    case 'stage':
      return sorted.sort(
        (a, b) =>
          PIPELINE_STAGES.indexOf(a.stage) - PIPELINE_STAGES.indexOf(b.stage) || byName(a, b),
      );
    case 'activity':
      return sorted.sort((a, b) => {
        if (a.lastActivityAt === b.lastActivityAt) return byName(a, b);
        if (a.lastActivityAt === null) return 1;
        if (b.lastActivityAt === null) return -1;
        return b.lastActivityAt.localeCompare(a.lastActivityAt);
      });
  }
}

/** The tags present on a pipeline, once each, for the filter's options. */
export function tagsOn(rows: readonly SearchCandidate[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const row of rows) for (const tag of row.tags) seen.set(tag.id, tag.name);
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

// --- the conversations list: five chips over two columns -----------------

export type ConversationFilter = 'all' | 'candidates' | 'clients' | 'awaiting' | 'escalated';
export const CONVERSATION_FILTERS: readonly ConversationFilter[] = [
  'all',
  'candidates',
  'clients',
  'awaiting',
  'escalated',
];

export const CONVERSATION_FILTER_LABELS: Record<ConversationFilter, string> = {
  all: 'All',
  candidates: 'Candidates',
  clients: 'Clients',
  awaiting: 'Awaiting code',
  escalated: 'Escalated',
};

/**
 * Each chip is a party type or a gate state — the two columns the list already shows. "Escalated"
 * is the gate's `blocked`: the escape hatch after a holding reply, and the badge already says
 * "Escalated" for it (screens/badges.tsx).
 */
export function conversationFilterQuery(filter: ConversationFilter): {
  party?: PartyType;
  gate?: GateState;
} {
  switch (filter) {
    case 'all':
      return {};
    case 'candidates':
      return { party: 'candidate' };
    case 'clients':
      return { party: 'client_contact' };
    case 'awaiting':
      return { gate: 'awaiting_code' };
    case 'escalated':
      return { gate: 'blocked' };
  }
}
